import { useEffect } from "react";
import { useProgressoEnvio } from "../../../utils/solicitacoes/progressoEnvio";

function formatarTamanho(bytes) {
    if (!bytes) return null;
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/**
 * Carregamento em tela cheia enquanto uma nova solicitação é criada e seus anexos sobem — mostra
 * a etapa atual, o anexo da vez (nome, tamanho, % enviado) e o total. Bloqueia a tela para o
 * usuário não sair no meio (o que deixaria a solicitação sem os anexos restantes).
 */
export default function EnvioProgressoOverlay() {
    const progresso = useProgressoEnvio();

    // Fechar/recarregar a aba no meio do envio interrompe os uploads que faltam.
    useEffect(() => {
        if (!progresso) return undefined;
        const avisar = e => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", avisar);
        return () => window.removeEventListener("beforeunload", avisar);
    }, [progresso]);

    if (!progresso) return null;

    const { titulo, subtitulo, anexoAtual, totalAnexos, arquivo, percentualArquivo } = progresso;
    const enviandoAnexo = totalAnexos > 0 && anexoAtual > 0;
    // Geral: anexos já concluídos + fração do atual.
    const percentualGeral = enviandoAnexo
        ? Math.round((((anexoAtual - 1) + percentualArquivo / 100) / totalAnexos) * 100)
        : null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center backdrop-blur-[1px] p-4" role="alertdialog" aria-live="polite" aria-busy="true">
            <div className="w-full max-w-md rounded-xl bg-card border border-border shadow-soft p-6 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                    <i className="fa fa-circle-notch fa-spin text-primary text-2xl shrink-0" />
                    <div className="min-w-0">
                        <h2 className="font-display text-base font-semibold text-foreground">{titulo}</h2>
                        {subtitulo && <p className="text-sm text-muted-foreground">{subtitulo}</p>}
                    </div>
                </div>

                {enviandoAnexo && (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between gap-2 text-xs">
                                <span className="truncate text-foreground font-medium" title={arquivo?.nome}>
                                    <i className="fa fa-paperclip mr-1 text-muted-foreground" />
                                    {arquivo?.nome || "Arquivo"}
                                </span>
                                <span className="shrink-0 text-muted-foreground">
                                    {[formatarTamanho(arquivo?.tamanho), `${percentualArquivo}%`].filter(Boolean).join(" · ")}
                                </span>
                            </div>
                            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${percentualArquivo}%` }} />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Anexo {anexoAtual} de {totalAnexos}</span>
                                <span>{percentualGeral}%</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                <div className="h-full bg-primary/60 transition-[width] duration-200" style={{ width: `${percentualGeral}%` }} />
                            </div>
                        </div>
                    </div>
                )}

                <p className="text-xs text-muted-foreground">Não feche nem saia desta página até o envio terminar.</p>
            </div>
        </div>
    );
}
